/* eslint-disable @typescript-eslint/camelcase */

import { Store } from 'vuex';
import { VueRouter } from 'vue-router/types/router.d';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  FirebaseLesson,
  Lang,
  Lesson,
  Level,
  LevelPersonalized,
  PersonalizedCourse,
  Phrase,
  PhraseResponse,
  Quiz,
  QuizPhrase,
  Skill,
  Unit,
  UnitPersonalized,
  RapidTestResponse,
} from '@/store/types';
import BbTools from '@/tools/bluebird';

import shuffle from 'lodash.shuffle';
import firebase from 'firebase';
import xml2js from 'xml2js';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import RoutePaths from '@/router/route_paths';

import DocumentData = firebase.firestore.DocumentData;

// eslint-disable-next-line @typescript-eslint/no-var-requires
const BB_ICON = require('../../public/img/bluebird_icon_150.png');

require('firebase/firestore');

const QUIZ_LISTENING = {
  id: 0,
  type: 'listening',
  label: 'Listening',
} as Quiz;
const QUIZ_READING = {
  id: 1,
  type: 'reading',
  label: 'Reading',
} as Quiz;
const QUIZ_AUDIO_MATCH1 = {
  id: 2,
  type: 'audiomatch1',
  label: 'Audio Match 1',
} as Quiz;
const QUIZ_AUDIO_MATCH2 = {
  id: 3,
  type: 'audiomatch2',
  label: 'Audio Match 2',
} as Quiz;
const QUIZ_PHRASE_MATCH = {
  id: 4,
  type: 'phrasematch',
  label: 'Phrase Match',
} as Quiz;
const QUIZ_TRANSLATION1 = {
  id: 5,
  type: 'translation1',
  label: 'Translation 1',
} as Quiz;
const QUIZ_TRANSLATION2 = {
  id: 6,
  type: 'translation2',
  label: 'Translation 2',
} as Quiz;
const QUIZ_SPELLING = {
  id: 7,
  type: 'spelling',
  label: 'Spelling',
} as Quiz;
const QUIZ_PHRASE_MATCH2 = {
  id: 8,
  type: 'phrasematch2',
  label: 'Phrase Match 2',
} as Quiz;

export default {
  DRILLS: [
    'flashcards',
  ],
  QUIZ_LISTENING,
  QUIZ_READING,
  QUIZ_AUDIO_MATCH1,
  QUIZ_AUDIO_MATCH2,
  QUIZ_PHRASE_MATCH,
  QUIZ_TRANSLATION1,
  QUIZ_TRANSLATION2,
  QUIZ_SPELLING,
  QUIZ_PHRASE_MATCH2,

  ASIA_LANGS: ['25', '35', '12', '21'],
  LEVELS_BLUEBIRD: ['1', '2', '3', '4', '5'],

  LEVEL_COURSE_PERSONALIZED: '0',
  LEVEL_RAPID_TEST: '6',
  COURSE_DAILY_LESSONS: '26807',
  LEVEL_DAILY_LESSONS: '432764',
  PHRASE_HIDDEN: '*****',

  // timeouts
  TO_START_PLAYING: 700,
  TO_START_RECORDING: 500,
  TO_AFTER_RECORDED: 700,
  TO_VIDEO_LOADING: 4000,
  TO_HIDE_ACTIVITY: 300,
  TO_AUDIO_START: 500,

  async setNewLangIL(store: Store<any>, langIId: string, langLId: string | null) {
    let langI: Lang = store.state.langsI.find((lang: Lang) => langIId === lang.id);
    if (!langI) {
      langI = BbTools.DEFAULT_LANG_I;
    }
    if (!store.state.langI || store.state.langI.id !== langI.id) {
      store.commit('setLangI', langI);
      await Promise.all([
        BbTools.loadTranslations(store, langI),
        BbTools.loadLanguagesILearn(store, langI),
        BbTools.loadSkillLevels(store, langI),
      ]);
      localStorage.setItem(BbTools.LS_LAST_LANG_I, JSON.stringify(langI));
    }
    let langL: Lang | null = null;
    if (langLId) {
      langL = store.state.langsL.find((lang: Lang) => langLId === lang.id);
      if (!langL) {
        langL = null;
      }
    }
    store.commit('setLangL', langL);
    this.saveMyCourses(store, this.getMyCoursesWithLangL(store, langL));
  },

  getMyCoursesWithLangL(store: Store<any>, langL: Lang | null = null): Array<string> {
    if (store.state.user && store.state.user.myCourses.length > 0) {
      if (!langL || store.state.user.myCourses.indexOf(langL.id) !== -1) {
        return store.state.user.myCourses;
      }
      return [...store.state.user.myCourses, langL.id];
    }
    if (langL) {
      return [langL.id];
    }
    return [];
  },

  async saveMyCourses(store: Store<any>, myCourses: Array<string>): Promise<void> {
    store.commit('setUser', { ...store.state.user, myCourses });
    return firebase.firestore().collection('users')
      .doc(store.state.user.uid).set({ myCourses: myCourses.toString() }, { merge: true });
  },

  async loadMainLevels(store: Store<any>, langI: Lang, langL: Lang): Promise<Array<Level>> {
    const params = BbTools.getDefaultParams();
    params.append('ilang', langI!.id);
    params.append('llang', langL!.id);
    return axios.post(store.getters.URL_LEVELS, params, BbTools.bbHeaders)
      .then(async (response: AxiosResponse) => {
        const responseJson = response.data;
        if (responseJson.success !== true) {
          store.commit('setAlertMessage', { text: 'Levels error #1' });
          return new Array<Level>();
        }
        const levelsFromResponse: Array<Level> = responseJson.data;
        const levels = new Array<Level>();
        const idLangI = BbTools.getILangAsLLang(langI!.id);
        levelsFromResponse.forEach((level: Level) => {
          if (this.LEVELS_BLUEBIRD.indexOf(level.obj_id) !== -1
            && store.state.levelsAllowed[`lang${idLangI}`]
            && store.state.levelsAllowed[`lang${langL!.id}`]
            && store.state.levelsAllowed[`lang${idLangI}`].indexOf(level.obj_id) !== -1
            && store.state.levelsAllowed[`lang${langL!.id}`].indexOf(level.obj_id) !== -1) {
            level.image = `${store.getters.ipLearn}/files/images/bb/levels/${store.state.langL.id}-${level.obj_id}-hq.jpg${BbTools.APP_POSTFIX}`;
            levels.push(level);
          }
        });
        return levels;
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Levels main error #1', error });
        return new Array<Level>();
      });
  },

  async loadLevels(store: Store<any>, langI: Lang | null = null, langL: Lang | null = null,
    lessonLast: FirebaseLesson | null = null): Promise<void> {
    if (!langI) {
      langI = store.state.langI;
    }
    if (!langL) {
      langL = store.state.langL;
    }

    const levelsQuery = firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('levels')
      .where('lang_l', '==', langL!.id);
    const levelsStored = await levelsQuery.get();

    let levels = await this.loadMainLevels(store, langI!, langL!);

    const personalizedLevel = {
      obj_id: this.LEVEL_COURSE_PERSONALIZED,
      text: store.state.tr.PersonalizedCourses,
      image: `${store.getters.ipLearn}/files/images/bb/levels/${store.state.langL.id}-p-hq.jpg${BbTools.APP_POSTFIX}`,
      units: [],
      theme: '',
      id: '-1',
      kids: false,
      query_string: '',
      started: false,
      completed: false,
      score: 0,
      score_all: 0,
      order: '0',
    } as Level;

    if (levels.length === 5 || lessonLast) {
      let units = new Array<Unit>();
      if (lessonLast) {
        // when lessonLast is used - there is no information about selected day
        // so load just one unit for daily lesson
        let order = '1';
        let index = 0;
        let lastInLevel = false;
        const skillDaily = store.state.skills
          .find((skill: Skill) => skill.label === lessonLast.unit_label);
        if (skillDaily) {
          order = skillDaily.order;
          index = parseInt(order, 10) - 1;
          lastInLevel = index === 3;
        }
        units = [{
          // eslint-disable-next-line @typescript-eslint/camelcase
          unit_text: lessonLast.unit_label,
          unit: lessonLast.unit_id,
          isPersonalized: false,
          isDaily: true,
          order,
          // eslint-disable-next-line @typescript-eslint/camelcase
          lesson_id: lessonLast.unit_lesson_id,
          level: order,
          id: lessonLast.unit_id,
          started: true,
          completed: false,
          score: 0,
          // eslint-disable-next-line @typescript-eslint/camelcase
          score_all: 0,
          image: `${store.getters.ipLearn}/files/images/bb/levels/293-p-hq.jpg${BbTools.APP_POSTFIX}`,
          complete: 0,
          // eslint-disable-next-line @typescript-eslint/camelcase
          last_in_level: lastInLevel,
        }];
      } else {
        units = await this.loadUnitsDaily(store);
      }
      if (units.length === 3 || lessonLast) {
        const dailyLesson = {
          obj_id: this.LEVEL_DAILY_LESSONS,
          text: 'Daily Lesson',
          image: `${store.getters.ipLearn}/files/images/bb/levels/293-p-hq.jpg${BbTools.APP_POSTFIX}`,
          units,
          theme: '',
          id: this.LEVEL_DAILY_LESSONS,
          kids: false,
          query_string: '',
          started: false,
          completed: false,
          score: 0,
          score_all: 0,
          order: '0',
        } as Level;
        levels = [dailyLesson, personalizedLevel, ...levels];
        // if(props.level && props.level.obj_id === LEVEL_DAILY_LESSONS) {
        store.commit('setLevelDaily', dailyLesson);
      } else {
        levels = [personalizedLevel, ...levels];
      }
    }
    const rapidTestLevel = {
      obj_id: this.LEVEL_RAPID_TEST,
      text: store.state.tr.RapidTest,
      image: `${store.getters.ipLearn}/files/images/bb/quiz/quiz-5.jpg${BbTools.APP_POSTFIX}`,
      units: [],
      theme: '',
      id: '-1',
      kids: false,
      query_string: '',
      started: false,
      completed: false,
      score: 0,
      score_all: 0,
      order: '0',
    };
    levels.push(rapidTestLevel);
    levels.forEach((level: Level) => {
      levelsStored.forEach((doc: firebase.firestore.DocumentSnapshot) => {
        const docData: DocumentData | undefined = doc.data();
        if (docData!.level_id === level.obj_id) {
          if (level.obj_id === this.LEVEL_DAILY_LESSONS && docData!.score_at
            // eslint-disable-next-line no-underscore-dangle
            && new Date().toDateString() === new Date(docData!.score_at._seconds * 1000)
              .toDateString()) {
            level.started = true;
            level.completed = docData!.completed;
            level.score = docData!.score;
            level.score_all = docData!.score_all;
          } else if (level.obj_id !== this.LEVEL_DAILY_LESSONS) {
            level.started = true;
            level.completed = docData!.completed;
            level.score = docData!.score;
            level.score_all = docData!.score_all;
          }
        }
      });
    });
    store.commit('setLevels', levels);
  },

  async loadCoursesPersonalized(store: Store<any>, langI: Lang | null = null,
    langL: Lang | null = null, updateLastAction = true): Promise<void> {
    if (!langI) {
      langI = store.state.langI;
    }
    if (!langL) {
      langL = store.state.langL;
    }
    if (updateLastAction) {
      await this.updateLastActionTime(store);
    }

    const params = BbTools.getDefaultParams();
    params.append('_a', 'mycourses');
    params.append('fbtoken', store.state.firebaseToken);
    params.append('v2', 'true');
    await axios.post(store.getters.URL_PERSONALIZED_COURSES, params, BbTools.bbHeaders)
      .then((response: AxiosResponse) => {
        const result = new Array<PersonalizedCourse>();
        const responseJson = response.data;
        if (responseJson.error) {
          if (responseJson.message === 'no course was created') {
            store.commit('setAlertMessage', { text: store.state.tr.PleaseRestart });
          } else {
            store.commit('setAlertMessage', { text: 'Personalized courses error #1' });
          }
        } else if (responseJson.my_courses) {
          responseJson.my_courses.forEach((course: PersonalizedCourse) => {
            if (course.iLangId.toString() === langI!.id
              && course.lLangId.toString() === langL!.id) {
              result.push(course);
            }
          });
        }
        store.commit('setCoursesPersonalized', result);
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Personalized courses error #2', error });
      });
  },

  async deletePersonalizedCourse(store: Store<any>, courseId: number): Promise<void> {
    await this.updateLastActionTime(store);
    const params = BbTools.getDefaultParams();
    params.append('_a', 'delete');
    params.append('fbtoken', store.state.firebaseToken);
    params.append('id', courseId.toString());
    await axios.post(store.getters.URL_PERSONALIZED_COURSES, params, BbTools.bbHeaders)
      .then((response: AxiosResponse) => {
        const responseJson = response.data;
        if (responseJson.error) {
          if (responseJson.message === 'Course Deleted') {
            // everything is ok
          } else if (responseJson.message === 'no course was created') {
            store.commit('setAlertMessage', { text: store.state.tr.PleaseRestart });
          } else {
            store.commit('setAlertMessage', {
              text: 'Delete personalized error #1',
              error: responseJson.error,
            });
          }
        }
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Delete personalized error #2', error });
      });
  },

  async loadLevelsPersonalized(store: Store<any>): Promise<void> {
    const levelsQuery = firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('levels')
      .where('lang_l', '==', store.state.langL.id);
    const levelsStored = await levelsQuery.get();

    const params = BbTools.getDefaultParams();
    params.append('course_id', store.state.personalizedCourse.id);
    await axios.post(store.getters.URL_LEVELS_PERSONALIZED, params, BbTools.bbHeaders)
      .then((response: AxiosResponse) => {
        const responseJson = response.data;
        const levelsPersonalized = new Array<Level>();
        const result = responseJson.data.sort(BbTools.compareByOrder);
        result.forEach((level: LevelPersonalized) => {
          level.LevelName = BbTools.replaceAll(store.state.tr.WeekNumber,
            '##number##', level.order);
          level.image = `${store.getters.ipLearn}/files/images/bb/weeks/w-${level.order}-hq.jpg${BbTools.APP_POSTFIX}`;
          let started = false;
          let completed = false;
          let score = 0;
          let score_all = 0;
          levelsStored.forEach((doc) => {
            const docData = doc.data();
            if (docData.level_id === level.id) {
              started = true;
              completed = docData.completed;
              score = docData.score;
              score_all = docData.score_all;
            }
          });
          levelsPersonalized.push({
            text: level.LevelName,
            obj_id: level.id,
            order: level.order,
            image: level.image,
            theme: '',
            id: '-1',
            kids: false,
            query_string: '',
            started,
            completed,
            score,
            score_all,
          });
        });
        store.commit('setLevelsPersonalized', levelsPersonalized);
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Personalized levels error #2', error });
      });
  },

  async loadUnits(store: Store<any>, langI: Lang | null = null,
    langL: Lang | null = null, levelId: string | null = null): Promise<void> {
    if (!langI) {
      langI = store.state.langI;
    }
    if (!langL) {
      langL = store.state.langL;
    }
    if (!levelId) {
      levelId = store.state.level.obj_id || store.state.unit.level;
    }

    const unitsQuery = firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('units')
      .where('lang_l', '==', langL!.id)
      .where('level_id', '==', levelId);
    const unitsStored = await unitsQuery.get();

    if (store.state.levelDaily && levelId === this.LEVEL_DAILY_LESSONS
      && store.state.levelDaily.units.length > 0) {
      const units = new Array<Unit>();
      const unitsDaily = store.state.levelDaily.units.slice()
        .sort(BbTools.compareByOrder);
      unitsDaily.forEach((unit: UnitPersonalized, index: number): void => {
        let started = false;
        let completed = false;
        let score = 0;
        let score_all = 0;
        unitsStored.forEach((doc) => {
          const docData = doc.data();
          if (docData.unit_id === unit.id) {
            started = true;
            completed = docData.completed;
            score = docData.score;
            score_all = docData.score_all;
          }
        });
        units.push({
          unit_text: unit.unit_text,
          unit: unit.id,
          isPersonalized: false,
          isDaily: true,
          order: unit.order,
          lesson_id: unit.id,
          level: this.LEVEL_DAILY_LESSONS,
          id: unit.id,
          started,
          completed,
          score,
          score_all,
          image: this.getUnitPersonalizedIconSource(store, parseInt(unit.order, 10)),
          complete: unit.Quizzes / 100,
          // do not forget about removed Review unit
          last_in_level: index === unitsDaily.length - 2,
        });
      });
      store.commit('setUnits', units);
    } else if (store.state.personalizedCourse) {
      const params = BbTools.getDefaultParams();
      params.append('level_id', levelId!);
      params.append('fbtoken', store.state.firebaseToken);
      params.append('v2', 'true');
      await axios.post(store.getters.URL_UNITS_PERSONALIZED, params, BbTools.bbHeaders)
        .then((response: AxiosResponse) => {
          let responseJson = response.data;
          if (responseJson.success === false) {
            store.commit('setAlertMessage', { text: 'Units personalized error #1' });
            return;
          }
          const units = new Array<Unit>();
          responseJson = responseJson.data.sort(BbTools.compareByOrder);
          responseJson.forEach((unit: UnitPersonalized, index: number) => {
            let image = '';
            if (unit.order !== '6' && unit.order !== this.LEVEL_DAILY_LESSONS) {
              image = this.getUnitPersonalizedIconSource(store, parseInt(unit.order, 10));
            } else if (unit.order === '6') {
              image = `${store.getters.ipLearn}/files/images/bb/quiz/quiz-4.jpg${BbTools.APP_POSTFIX}`;
            } else {
              image = `${store.getters.ipLearn}/files/images/bb/quiz/quiz-5.jpg${BbTools.APP_POSTFIX}`;
            }
            let started = false;
            let completed = false;
            let score = 0;
            let score_all = 0;
            unitsStored.forEach((doc) => {
              const docData = doc.data();
              if (docData.unit_id === unit.id) {
                started = true;
                completed = docData.completed;
                score = docData.score;
                score_all = docData.score_all;
              }
            });
            units.push({
              unit_text: unit.UnitName,
              unit: unit.id,
              isPersonalized: true,
              isDaily: false,
              order: unit.order,
              lesson_id: unit.id,
              image,
              started,
              completed,
              score,
              score_all,
              id: unit.id,
              level: this.LEVEL_COURSE_PERSONALIZED,
              complete: unit.Quizzes / 100,
              // do not forget about removed Review unit
              last_in_level: index === responseJson.length - 2,
            });
          });
          store.commit('setUnits', units);
        })
        .catch((error) => {
          store.commit('setAlertMessage', { text: 'Units personalized error #2', error });
        });
    } else {
      const params = BbTools.getDefaultParams();
      params.append('ilang', langI!.id);
      params.append('llang', langL!.id);
      params.append('level', levelId!);
      params.append('fbtoken', store.state.firebaseToken);
      params.append('v2', 'true');
      await axios.post(store.getters.URL_UNITS, params, BbTools.bbHeaders)
        .then((response: AxiosResponse) => {
          const responseJson = response.data;
          if (!Array.isArray(responseJson)) {
            store.commit('setAlertMessage', { text: 'Units error #1' });
            return;
          }
          const units = new Array<Unit>();
          responseJson.forEach((unit: Unit) => {
            if (unit.unit !== '137' && unit.unit !== '153' && unit.unit !== '248') {
              unitsStored.forEach((doc) => {
                const docData = doc.data();
                if (docData.unit_id === unit.unit) {
                  unit.started = true;
                  unit.completed = docData.completed;
                  unit.score = docData.score;
                  unit.score_all = docData.score_all;
                }
              });
              unit.image = this.getUnitIconSource(store, unit.unit);
              units.push(unit);
            }
          });
          store.commit('setUnits', units);
        })
        .catch((error) => {
          store.commit('setAlertMessage', { text: 'Units error #2', error });
        });
    }
  },

  async loadUnitsDaily(store: Store<any>, day: number | null = null): Promise<Array<Unit>> {
    const dailyURL = `${store.getters.URL_DAILY_COURSES}?ilang=${store.state.langI.id}&llang=${store.state.langL.id}&level=`;
    let dayParam = '';
    if (day) {
      dayParam = `&day=${day}`;
    }
    return Promise.all([
      this.loadUnitDaily(store, dailyURL, dayParam, 1),
      this.loadUnitDaily(store, dailyURL, dayParam, 2),
      this.loadUnitDaily(store, dailyURL, dayParam, 3),
    ]);
  },

  async loadUnitDaily(store: Store<any>, url: string, dayParam: string, i: number) {
    return axios.get(url + i + dayParam, BbTools.bbHeaders)
      .then((response: AxiosResponse) => {
        const dailyCourse = response.data;
        const unit = dailyCourse.unit ? dailyCourse.unit : dailyCourse;
        unit.image = `${store.getters.ipLearn}/files/images/bb/levels/293-p-hq.jpg${BbTools.APP_POSTFIX}`;
        unit.level = i;
        unit.unit = unit.id;
        unit.unit_text = store.state.skills
          .find((skill: Skill) => skill.order === i).label;
        unit.order = store.state.skills
          .find((skill: Skill) => skill.order === i).order;
        return unit;
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', {
          text: 'Daily unit error #2',
          error,
        });
      });
  },

  async setUnitsDaily(store: Store<any>, day: number | null = null): Promise<void> {
    const units = await this.loadUnitsDaily(store, day);
    const levelDaily = { ...store.state.levelDaily, units };
    store.commit('setLevelDaily', levelDaily);
    await this.loadUnits(store);
  },

  async loadLessons(store: Store<any>, langI: Lang | null = null, langL: Lang | null = null,
    levelId: string | null = null, unitId: string | null = null,
    unitLessonId: string | null = null): Promise<void> {
    if (!langI) {
      langI = store.state.langI;
    }
    if (!langL) {
      langL = store.state.langL;
    }
    if (!levelId) {
      levelId = store.state.level.obj_id;
    }
    if (!unitId) {
      unitId = store.state.unit.unit;
    }
    if (!unitLessonId) {
      unitLessonId = store.state.unit.lesson_id;
    }

    const idLangIForRequest = levelId === '6' ? BbTools.getILangAsLLang(langI!.id) : langI!.id;

    const lessonsQuery = firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('lessons')
      .where('lang_l', '==', langL!.id)
      .where('level_id', '==', levelId)
      .where('unit_id', '==', unitId);
    const lessonsStored = await lessonsQuery.get();

    const params = BbTools.getDefaultParams();
    params.append('cmd', 'card');
    params.append('fbtoken', store.state.firebaseToken);
    params.append('v2', 'true');
    let url = `${store.getters.URL_PHRASES}?num_pars[]=${levelId}&num_pars[]=${unitId}&num_pars[]=${unitLessonId}&num_pars[]=${idLangIForRequest}&num_pars[]=${langL!.id}`;
    if (levelId === this.LEVEL_DAILY_LESSONS) {
      url = store.getters.URL_PHRASES_PERSONALIZED;
      params.append('course_id', this.COURSE_DAILY_LESSONS);
      params.append('unit_id', unitId!);
    } else if (store.state.personalizedCourse) {
      url = store.getters.URL_PHRASES_PERSONALIZED;
      params.append('course_id', store.state.personalizedCourse.id);
      params.append('unit_id', unitId!);
    }
    return axios.post(url, params, BbTools.bbHeaders)
      .then((response: AxiosResponse) => {
        try {
          xml2js.parseString(response.data, (error: Error, result: any) => {
            if (result && result.Page && result.Page.Card) {
              const lessonsToSave = new Array<Lesson>();
              let lessons = new Array<string>();
              const phrases = new Array<Phrase>();
              result.Page.Card.forEach((phraseResponse: PhraseResponse) => {
                const phrase = phraseResponse.$;
                if (lessons.indexOf(phrase.lesson_id) === -1) {
                  lessons.push(phrase.lesson_id);
                }
                if ((!store.state.personalizedCourse || levelId !== this.LEVEL_DAILY_LESSONS)
                  && lessons.indexOf(phrase.lesson_id) === -1) {
                  lessons.push(phrase.lesson_id);
                }
                phrase.word = this.formatPhrase(phrase.word);
                phrase.native_word = this.formatPhrase(phrase.native_word);
                phrase.audio += BbTools.APP_POSTFIX;
                phrase.audio_native = BbTools.replaceAll(phrase.audio,
                  `/${store.state.langL.id}/`, `/${store.state.langI.id}/`);
                // eslint-disable-next-line @typescript-eslint/camelcase
                phrase.word_image += BbTools.APP_POSTFIX;
                phrase.bbImage = `${store.getters.ipLearn}/files/images/pro/${phrase.id}.jpg${BbTools.APP_POSTFIX}`;
                phrases.push(phrase);
              });
              if (store.state.personalizedCourse || (store.state.levelDaily
                && store.state.level.id === this.LEVEL_DAILY_LESSONS)) {
                lessons = [unitId!]; // only one lesson for personalized courses
              }
              store.commit('setPhrases', phrases);

              lessons.forEach((lesson, index) => {
                let lexicalDifficulty = '2';
                if (store.state.levelDaily && levelId === this.LEVEL_DAILY_LESSONS) {
                  lexicalDifficulty = store.state.levelDaily.units
                    .findIndex((unit: Unit) => unit.unit === store.state.unit.unit) + 1;
                }
                if (!store.state.personalizedCourse && !(store.state.levelDaily
                  && levelId === this.LEVEL_DAILY_LESSONS)) {
                  const lessonPhrase = phrases.find((item) => item.lesson_id === lesson);
                  if (lessonPhrase && lessonPhrase.lexical_difficulty) {
                    lexicalDifficulty = lessonPhrase.lexical_difficulty;
                  }
                }
                const lessonToSave = {
                  id: lesson,
                  image: `${store.getters.ipLearn}/files/images/bb/lessons/lesson-${index + 1}.jpg${BbTools.APP_POSTFIX}`,
                  quizImage: `${store.getters.ipLearn}/files/images/bb/quiz/quiz-${index + 1}.jpg${BbTools.APP_POSTFIX}`,
                  lexicalDifficulty: parseFloat(lexicalDifficulty),
                  text: BbTools.replaceAll(store.state.tr.Lesson, '##lesson_number##', (index + 1).toString()),
                  flashcardsStatus: result.Page.$[`flashcardsStatus_lesson_${lesson}`],
                } as Lesson;
                lessonsStored.forEach((doc) => {
                  const docData = doc.data();
                  if (docData.lesson_id === lesson) {
                    lessonToSave.started = true;
                    lessonToSave.completed = docData.completed;
                    lessonToSave.score = docData.score;
                  }
                });
                lessonsToSave.push(lessonToSave);
              });
              store.commit('setLessons', lessonsToSave);
            } else {
              store.commit('setAlertMessage', {
                text: 'Lessons error #1',
                error,
              });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', {
            text: 'Lessons error #2',
            error,
          });
        }
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', {
          text: 'Lessons error #3',
          error,
        });
      });
  },

  formatPhrase(string: string): string {
    string = BbTools.replaceAll(string, /\s\s+/, ' ');
    string = BbTools.replaceAll(string, /\.\.\./, '... ');
    string = BbTools.replaceAll(string, '…  ', '... ');
    string = BbTools.replaceAll(string, '‥  ', '.. ');
    string = BbTools.replaceAll(string, '”', '"');
    string = BbTools.replaceAll(string, '“', '"');
    string = BbTools.replaceAll(string, '„', '"');
    string = BbTools.replaceAll(string, '«', '"');
    string = BbTools.replaceAll(string, '»', '"');
    string = BbTools.replaceAll(string, '’', '\'');
    string = BbTools.replaceAll(string, '‘', '\'');
    return string;
  },

  async loadLesson(store: Store<any>, router: VueRouter,
    lessonFirebase: FirebaseLesson, fromHistory = false): Promise<void> {
    if (router.currentRoute.path === RoutePaths.AUDIO
      && store.state.lesson && store.state.unit && store.state.level && store.state.langL
      && store.state.lesson.id === lessonFirebase.lesson_id
      && store.state.unit.unit === lessonFirebase.unit_id
      && store.state.level.obj_id === lessonFirebase.level_id
      && store.state.langL.id === lessonFirebase.lang_l) {
      return;
    }
    store.commit('setMainLoading', true);
    await this.setNewLangIL(store, lessonFirebase.lang_i, lessonFirebase.lang_l);
    store.commit('setMainLoading', true);
    if (lessonFirebase.personalized && lessonFirebase.personalized !== '') {
      await Promise.all([
        this.loadLevels(store),
        this.loadCoursesPersonalized(store),
      ]);
      const personalizedCourse = store.getters
        .findPersonalizedCourseById(lessonFirebase.personalized);
      if (!personalizedCourse) {
        store.commit('setAlertMessage', { text: 'Load lesson: personalized course error' });
        return;
      }
      store.commit('setPersonalizedCourse', personalizedCourse);
      await this.loadLevelsPersonalized(store);
      const levelLoad = store.state.levelsPersonalized
        .find((level: Level) => level.obj_id.toString() === lessonFirebase.level_id.toString());
      if (!levelLoad) {
        store.commit('setAlertMessage', { text: 'Load lesson: personalized level error' });
        return;
      }
      store.commit('setLevel', levelLoad);
    } else {
      store.commit('setLevelsPersonalized', []);
      store.commit('setPersonalizedCourse', null);
      if (lessonFirebase.level_id.toString() === this.LEVEL_DAILY_LESSONS) {
        await this.loadLevels(store, null, null, lessonFirebase);
      } else {
        await this.loadLevels(store);
      }
      const levelLoad = store.state.levels
        .find((level: Level) => level.obj_id.toString() === lessonFirebase.level_id.toString());
      if (!levelLoad) {
        store.commit('setAlertMessage', { text: 'Load lesson: level error' });
        return;
      }
      store.commit('setLevel', levelLoad);
    }
    await this.loadUnits(store);
    const unitLoad = store.state.units
      .find((unit: Unit) => unit.unit.toString() === lessonFirebase.unit_id.toString());
    if (!unitLoad) {
      store.commit('setAlertMessage', { text: 'Load lesson: personalized unit error' });
      return;
    }
    store.commit('setUnit', unitLoad);
    await this.loadLessons(store);
    store.commit('setLesson', null);
    if (router.currentRoute.path !== RoutePaths.LESSONS) {
      router.push(RoutePaths.LESSONS);
    }
  },

  updateLastActionTime(store: Store<any>): Promise<void> {
    return firebase.firestore().collection('users').doc(store.state.user.uid)
      .set({
        lastActionTime: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true });
  },

  getLessonParamsEncoded(langIId: string, langLId: string, lessonId: string) {
    return Base64.stringify(Utf8.parse(`ilang=${langIId}&llang=${langLId}&lesson=${lessonId}`));
  },

  getUnitIconSource(store: Store<any>, unitId: string): string {
    if (parseInt(unitId, 10) >= 1 && parseInt(unitId, 10) <= 421) {
      return `${store.getters.ipLearn}/files/images/bb/units/${unitId}-hq.jpg${BbTools.APP_POSTFIX}`;
    }
    return BB_ICON;
  },

  getUnitPersonalizedIconSource(store: Store<any>, order: number, dailyLesson = false): string {
    if (order >= 1 && order <= 7) {
      return `${store.getters.ipLearn}/files/images/bb/days/d-${order}-hq.jpg${BbTools.APP_POSTFIX}`;
    }
    if (dailyLesson) {
      return `${store.getters.ipLearn}/files/images/bb/quiz/quiz-4.jpg${BbTools.APP_POSTFIX}`;
    }
    return BB_ICON;
  },

  getUnitDailyIconSource(store: Store<any>, order: number): string {
    return this.getUnitPersonalizedIconSource(store, order, true);
  },

  getFirebaseLessonName(store: Store<any>, langLId: string | null = null,
    lessonId: string | null = null): string {
    if (!langLId) {
      langLId = store.state.langL.id;
    }
    if (!lessonId) {
      lessonId = store.state.lesson ? store.state.lesson.id : null;
    }
    return Base64.stringify(Utf8.parse(`llang=${langLId}&lesson=${lessonId}`));
  },

  calculateAverageScore(scores: Array<number>): number {
    let sum = 0;
    let number = 0;
    for (let s = 0; s < scores.length; s += 1) {
      // eslint-disable-next-line no-prototype-builtins
      if (scores.hasOwnProperty(s)) {
        sum += scores[s];
        number += 1;
      }
    }
    if (number === 0) {
      return 0;
    }
    return Math.round((sum / number) * 100);
  },

  getPhraseWords(phrase: string, audioLength: number, store: Store<any>,
    drillMovie = false): Map<string, number> {
    if (!phrase) {
      return new Map<string, number>();
    }
    let wordsWithSpaces;
    if (this.ASIA_LANGS.indexOf(store.state.langL.id) !== -1 && !store.getters.isMediaLevel()
      && !store.getters.isMusicLevel() && !store.getters.isWotsLevel()) {
      wordsWithSpaces = phrase.split('');
    } else {
      wordsWithSpaces = phrase.split(/( |-)/);
    }
    const words = new Array<string>();
    for (let i = 0; i < wordsWithSpaces.length; i += 1) {
      const w = wordsWithSpaces[i];
      if (i < wordsWithSpaces.length - 4 && w === ' '
        && wordsWithSpaces[i + 1] === '' && wordsWithSpaces[i + 2] === '-'
        && wordsWithSpaces[i + 3] === '' && wordsWithSpaces[i + 4] === ' ') {
        words.push(' - ');
        i += 4;
      } else if (i < wordsWithSpaces.length - 2 && w === ' '
        && wordsWithSpaces[i + 1] === '' && wordsWithSpaces[i + 2] === '-') {
        words.push(' -');
        i += 2;
      } else if (i < wordsWithSpaces.length - 2 && w === '-'
        && wordsWithSpaces[i + 1] === '' && wordsWithSpaces[i + 2] === ' ') {
        words.push('- ');
        i += 2;
      } else if (w !== ' ' && w !== '') {
        if (w === '。' && words.length > 0) {
          words[words.length - 1] = `${words[words.length - 1]}。`;
        } else {
          words.push(w);
        }
      }
    }
    const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    const wordsLength = new Array<number>();
    let charsLength = 0;
    let playCharacterPassed = true;
    if (store.getters.isPlayLevel()) {
      if (words.length > 0 && words[0].indexOf('[') !== -1) {
        playCharacterPassed = false;
      }
    }
    words.forEach((word) => {
      let l = word.length;
      numbers.some((number) => {
        if (word.indexOf(number) !== -1) {
          l = 4;
          return true;
        }
        return false;
      });
      if (!playCharacterPassed) {
        l = 0;
        if (word.indexOf(']') !== -1) {
          playCharacterPassed = true;
        }
      }
      charsLength += l;
      wordsLength.push(l);
    });
    let prevWord = 250;
    let firstWord = true;
    const oneCharLength = audioLength / charsLength;
    const delayedMap = new Map();
    for (let wordIndex = 0; wordIndex < words.length; wordIndex += 1) {
      if (wordsLength[wordIndex] !== 0) {
        let resultString = '';
        for (let i = 0; i < words.length; i += 1) {
          let space = ' ';
          if (this.ASIA_LANGS.indexOf(store.state.langL.id) !== -1) {
            space = '';
          }
          if (words[i] === '-' || (i < words.length - 2 && words[i + 1] === '-')
            || words[i] === ' -' || (i < words.length - 2 && words[i + 1] === ' -')
            || words[i] === ' - ' || (i < words.length - 2 && words[i + 1] === ' - ')
            || words[i] === '- ' || (i < words.length - 2 && words[i + 1] === '- ')) {
            space = '';
          }

          if (wordIndex === i && words[i].indexOf('_') === -1 && words[i] !== '‎') {
            let redColor = true;
            resultString += '<span class="primary--text">';
            for (let l = 0; l < words[i].length; l += 1) {
              if (words[i].charAt(l) !== '.' && words[i].charAt(l) !== ','
                && words[i].charAt(l) !== '\'' && words[i].charAt(l) !== '-'
                && words[i].charAt(l) !== '!' && words[i].charAt(l) !== '?') {
                if (!redColor) {
                  redColor = true;
                  resultString += '</span><span class="primary--text">';
                }
                resultString += words[i].charAt(l);
              } else {
                if (redColor) {
                  redColor = false;
                  resultString += '</span><span class="link--text">';
                }
                resultString += words[i].charAt(l);
              }
            }
            resultString += '</span>';
            resultString += space;
          } else if (words[i] !== '‎') {
            resultString += `<span class="link--text">${words[i]}${space}</span>`;
          }
        }
        if (firstWord) {
          delayedMap.set(resultString, prevWord);
          firstWord = false;
        } else {
          delayedMap.set(resultString, prevWord + (oneCharLength * words[wordIndex - 1].length));
          prevWord += oneCharLength * words[wordIndex - 1].length;
        }
      }
    }
    if (drillMovie) {
      delayedMap.set(phrase, audioLength + 250);
    } else if (store.getters.isPlayLevel()) {
      delayedMap.set('', audioLength);
    } else {
      delayedMap.set('', audioLength + 250);
    }
    return delayedMap;
  },
  compareAudio(blob: any, phraseId: string, store: Store<any>): Promise<number> {
    const params = new FormData();
    params.append('audioblob', blob);
    const url = `${store.getters.URL_COMPARE_AUDIO}?cmd=compare&str_pars[]=${new Date().getTime()}.flv&num_pars[]=${phraseId}&num_pars[]=${store.state.langL.id}`;
    return axios.post(url, params, BbTools.bbHeaders)
      .then((response: AxiosResponse): number => {
        let resultScore = -1;
        try {
          xml2js.parseString(response.data, (error: Error, result: any) => {
            if (error) {
              store.commit('setAlertMessage', { text: 'Compare error #1' });
            }
            if (result && result.Page && result.Page.word && result.Page.word.length > 0) {
              resultScore = parseFloat(result.Page.word[0].$.score);
            } else {
              store.commit('setAlertMessage', { text: 'Compare error #2' });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', { text: 'Compare error #3', error });
        }
        return resultScore;
      });
  },
  captureDevice(navigator: Navigator | Navigator, mediaConstraints: MediaStreamConstraints,
    successCallback: NavigatorUserMediaSuccessCallback,
    errorCallback: NavigatorUserMediaErrorCallback) {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia(mediaConstraints)
        .then((stream) => {
          successCallback(stream);
        })
        .catch((error) => {
          errorCallback(error);
        });
    } else if (navigator.getUserMedia) {
      navigator.getUserMedia(mediaConstraints, successCallback, errorCallback);
    } else {
      errorCallback(new MediaStreamError());
    }
  },
  getWordOrPhonetic(store: Store<any>, phrase: Phrase) {
    if (store.state.user.phoneticMode && phrase.phonetic && phrase.phonetic !== '') {
      return phrase.phonetic;
    }
    return phrase.word;
  },
  generateTranslationQuiz(store: Store<any>, phrase: Phrase, phrases: Array<Phrase>,
    quizType: number) {
    let choices: Array<string> = [];
    let phraseAnswer = [];

    choices = quizType === this.QUIZ_TRANSLATION2.id
      ? this.getWordOrPhonetic(store, phrase).trim().split(' ')
      : phrase.native_word.trim().split(' ');
    phraseAnswer = Object.assign([], choices);
    let randomIndex = null;

    do {
      randomIndex = BbTools.getRandomInt(phrases.length - 1);
      if (this.getWordOrPhonetic(store, phrases[randomIndex])
        === this.getWordOrPhonetic(store, phrase)) {
        randomIndex = null;
      }
    } while (randomIndex === null);

    let fakeChoicesTempWords = quizType === this.QUIZ_TRANSLATION2.id
      ? this.getWordOrPhonetic(store, phrases[randomIndex]).trim().split(' ')
      : phrases[randomIndex].native_word.trim().split(' ');

    fakeChoicesTempWords.forEach((v: string): void => {
      if (choices.indexOf(v) === -1) {
        choices.push(v);
      }
    });

    if (fakeChoicesTempWords.length <= 1) {
      randomIndex = BbTools.getRandomInt(phrases.length - 1);
      fakeChoicesTempWords = quizType === this.QUIZ_TRANSLATION2.id
        ? this.getWordOrPhonetic(store, phrases[randomIndex]).trim().split(' ')
        : phrases[randomIndex].native_word.trim().split(' ');
      fakeChoicesTempWords.forEach((v: string): void => {
        if (choices.indexOf(v) < 0) {
          choices.push(v);
        }
      });
    }
    return {
      phrase,
      quizType,
      choices: [],
      choicesString: shuffle(choices),
      phraseAnswer,
      wordWithoutSpelling: '',
      correctWords: -1,
      score: 100,
      nativeChoices: [],
      wrongAnswerPenalty: Math.floor(100 / choices.length),
    } as QuizPhrase;
  },
  generateQuizSpellingData(store: Store<any>, phrase: Phrase,
    sentence: string | null = null): QuizPhrase {
    let index = 0;
    if (!sentence) {
      sentence = this.getWordOrPhonetic(store, phrase);
    }
    sentence = sentence.replace(/\\u00A0/g, '');
    const arrayWithPersonWords = sentence.split(' ');
    const a: Array<string> = [];
    let longWordsArray: Array<string> = [];
    let wordDash = '';
    for (let i = 0; i < arrayWithPersonWords.length; i += 1) {
      // only long words
      let personWord = false;
      for (let q = i; q < arrayWithPersonWords.length; q += 1) {
        if (arrayWithPersonWords[q].indexOf(']') !== -1) {
          personWord = true;
          break;
        }
      }
      if (!personWord) {
        a.push(arrayWithPersonWords[i].replace(new RegExp(/[\s.?؟]/g), ''));
      }
    }
    if (a.length === 1 || a.length === 2) {
      longWordsArray = a;
    } else if (a.length >= 3) {
      for (let i = 0; i < a.length; i += 1) {
        // only long words
        if (a[i].length > 3 && a[i].indexOf('_') === -1) {
          longWordsArray.push(a[i]);
        }
      }
      if (longWordsArray.length === 0) {
        longWordsArray = a;
      }
    }
    if (longWordsArray.length === 0) {
      longWordsArray = a;
    }
    index = Math.round(Math.random() * (longWordsArray.length - 1));
    let word = longWordsArray[index];
    if (longWordsArray.length === 1 && word.indexOf('_') !== -1) {
      if (word.lastIndexOf('_') === word.length - 1) {
        word = word.slice(0, word.indexOf('_'));
      } else {
        word = word.slice(word.lastIndexOf('_') + 1, word.length);
      }
    }
    if (word.length > 0 && word.indexOf('_') === -1) {
      const wordIndex = a.indexOf(word);
      for (let i = 0; i < word.length; i += 1) {
        if (wordIndex > -1 && wordIndex + 1 < a.length && a[wordIndex + 1].length > 0 && '1234567890'.indexOf(a[wordIndex + 1].charAt(0)) !== -1) {
          wordDash += '—';
        } else {
          wordDash += 'ـ';
        }
      }
      const quizWord = word;
      const quizPhrase = sentence.replace(word, wordDash);
      const letters = quizWord.split('');
      return {
        phrase,
        quizType: this.QUIZ_SPELLING.id,
        choices: [],
        choicesString: shuffle(letters),
        nativeChoices: [],
        phraseAnswer: letters,
        wordWithoutSpelling: `\u200E${quizPhrase}`,
        correctWords: -1,
        score: 100,
        wrongAnswerPenalty: Math.floor(100 / letters.length),
      } as QuizPhrase;
    }
    return this.generateQuizSpellingData(store, phrase, sentence);
  },
  getRapidTestPhrases(store: Store<any>, langI: Lang, langL: Lang, sID: string, token: string) {
    // const url = `${store.getters.URL_RAPID_TEST}?_a=quiz&ilangId=
    // ${langI.id}&llangId=${langL.id}&sID=${sID}&fbtoken=${token}&v2=true`;
    const url = store.getters.URL_RAPID_TEST;
    const params = BbTools.getDefaultParams();
    params.append('_a', 'quiz');
    params.append('ilangId', langI!.id);
    params.append('llangId', langL!.id);
    params.append('sID', sID);
    params.append('fbtoken', token);
    params.append('v2', 'true');
    return axios.post(url, params, BbTools.bbHeaders)
      .then(async (response: AxiosResponse) => {
        const responseJson = response.data;
        if (responseJson.success !== true || responseJson.Error) {
          store.commit('setAlertMessage', { text: `Get RapidTest error #1 ${responseJson.message}` });
          return new Array<Phrase>();
        }
        return responseJson.data;
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Get RapidTest error #2', error });
        return new Array<Level>();
      });
  },

  async setAnswerAndGetNextRapidTestPhrase(
    store: Store<any>,
    router: VueRouter,
    quizType: number,
    word: string,
    nword = '',
    hsh = null,
  ): Promise<RapidTestResponse | null> {
    const langI = store.state.langI.id;
    const langL = store.state.langL.id;
    const token = store.state.firebaseToken;
    const sID = store.state.rapidTestSID;
    // 2. Spelling checking
    // _a=verifSpelling&word=t&xml=true&sID=lK5datApJxXCEJbgGWmeMNS5oD&ilangId=297&llangId=1

    // 3. Quiz Answer
    // _a=answer&word=word&xml=true&sID=&ilangId=297&llangId=1

    // 4. Phrase Match
    // _a=answer&word=Swing&xml=true&sID=&ilangId=297&llangId=1&nword=Swing

    let url = `${store.getters.URL_RAPID_TEST}?ilangId=${langI}&llangId=${langL}&sID=${sID}&fbtoken=${token}&v2=true`;

    if (quizType === QUIZ_SPELLING.id) {
      url += `&_a=verifSpelling&word=${word}`;
    } else if (quizType === QUIZ_PHRASE_MATCH.id) {
      url += `&_a=answer&word=${word}&nword=${nword}`;
    } else if (quizType === QUIZ_TRANSLATION2.id) {
      url += `&_a=verifTranslation2&word=${word}`;
    } else {
      url += `&_a=answer&word=${word}`;
    }
    if (hsh) {
      url += '&hsh=1';
    }

    const params = BbTools.getDefaultParams();
    try {
      const response = await axios.post(url, params, BbTools.bbHeaders);
      const responseJson = response.data;

      if (responseJson.success !== true || responseJson.Error) {
        store.commit('setAlertMessage', {
          text: `setAnswerAndGetNextRapidTestPhrase error #1 ${responseJson.message}`,
        });
        return null;
      }

      if (responseJson.data.message?.toUpperCase() === 'TEST ENDED.') {
        router.push({
          path: RoutePaths.RAPID_TEST_RESULTS,
          query: {
            fromRapidTest: 'true',
          },
        });
        return null;
      }

      return responseJson.data;
    } catch (error) {
      store.commit('setAlertMessage', {
        text: 'setAnswerAndGetNextRapidTestPhrase error #2',
        error,
      });
      return null;
    }
  },
};
